import React from 'react'
import { ChevronLeft, ChevronRight, Calendar, User } from 'lucide-react'
import { format, addWeeks, subWeeks, addMonths, subMonths, startOfWeek } from 'date-fns'

interface CalendarNavigationProps {
  currentDate: Date
  onDateChange: (date: Date) => void
  view: 'daily' | 'weekly' | 'monthly' | 'agenda'
  selectedKid?: string
  onKidFilterChange?: (kid: string | undefined) => void
  familyMembers: Array<{ username: string; display_name: string; emoji: string }>
}

export default function CalendarNavigation({
  currentDate,
  onDateChange,
  view,
  selectedKid,
  onKidFilterChange,
  familyMembers
}: CalendarNavigationProps) {
  
  const handlePrevious = () => {
    if (view === 'weekly') {
      onDateChange(subWeeks(currentDate, 1))
    } else if (view === 'monthly') {
      onDateChange(subMonths(currentDate, 1))
    } else {
      onDateChange(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000))
    }
  }

  const handleNext = () => {
    if (view === 'weekly') {
      onDateChange(addWeeks(currentDate, 1))
    } else if (view === 'monthly') {
      onDateChange(addMonths(currentDate, 1))
    } else {
      onDateChange(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))
    }
  }

  const handleToday = () => {
    const today = new Date()
    onDateChange(today)
  }

  const getDateRangeText = () => {
    if (view === 'weekly') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
      const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    } else if (view === 'monthly') {
      return format(currentDate, 'MMMM yyyy')
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    }
  }

  // Check if we're showing current week/month
  const isCurrentPeriod = () => {
    const today = new Date()
    if (view === 'weekly') {
      const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
      const todayWeekStart = startOfWeek(today, { weekStartsOn: 0 })
      return currentWeekStart.getTime() === todayWeekStart.getTime()
    } else if (view === 'monthly') {
      return currentDate.getMonth() === today.getMonth() && 
             currentDate.getFullYear() === today.getFullYear()
    } else {
      return currentDate.toDateString() === today.toDateString()
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
      {/* Date Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={handlePrevious}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Previous period"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="min-w-[200px] text-center">
          <h3 className="text-lg font-semibold">{getDateRangeText()}</h3>
          {!isCurrentPeriod() && (
            <button
              onClick={handleToday}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              Go to today
            </button>
          )}
        </div>
        
        <button
          onClick={handleNext}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Next period"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Kid Filter */}
      {onKidFilterChange && (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-600" />
          <select
            value={selectedKid || 'all'}
            onChange={(e) => onKidFilterChange(e.target.value === 'all' ? undefined : e.target.value)}
            className="px-3 py-1 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Kids</option>
            {familyMembers.map(member => (
              <option key={member.username} value={member.username}>
                {member.emoji} {member.display_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Today Button (prominent when not on current period) */}
      {!isCurrentPeriod() && (
        <button
          onClick={handleToday}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Today
        </button>
      )}
    </div>
  )
}